import { Link, useLocation, useParams } from 'react-router-dom'
import { Flag } from 'lucide-react'
import { todayInTimezone } from '@gd/shared/dates'
import { useAthlete } from '@/api/hooks'
import { usePlan } from '@/api/planHooks'
import { useOwnsAthlete } from '@/api/useOwnsAthlete'
import { currentBlock, planRunway } from '@/lib/planProgress'

const DEFAULT_TIMEZONE = 'America/Chicago'

/**
 * One slim line on Today while a plan is active: the goal, the runway and the block (redesign,
 * 2026-10-10). Today only since the simplify pass (2026-11-03): the Plan page leads with the same
 * facts, and repeating the line on every page cost a row on the phone. Nothing without a plan or
 * for someone else's athlete.
 */
export function PlanStrip() {
  const { slug } = useParams<{ slug: string }>()
  const { pathname } = useLocation()
  const { athlete } = useAthlete(slug)
  const owns = useOwnsAthlete(athlete?.id)
  const { data: plan } = usePlan(athlete?.id, owns)
  if (!plan || !athlete || !/\/today\/?$/.test(pathname)) return null

  const today = todayInTimezone(athlete.timezone ?? DEFAULT_TIMEZONE)
  const runway = planRunway(plan, today)
  const block = currentBlock(plan, today)
  const parts = [
    plan.goalRace ?? plan.name,
    runway.notStarted
      ? 'starts soon'
      : runway.weeksToGo != null
        ? runway.weeksToGo === 0
          ? `${runway.daysToGo} days to go`
          : `${runway.weeksToGo} weeks to go`
        : `week ${runway.week}`,
    block &&
      (block.weeks === 1
        ? block.block.name
        : `${block.block.name} week ${block.week} of ${block.weeks}`),
  ].filter(Boolean)

  return (
    <Link
      to={`/a/${slug}/plan`}
      className="mb-4 flex w-full items-center gap-2 rounded-md border border-line bg-surface-2/50 px-3 py-2 text-small text-ink-2 transition-colors duration-150 hover:bg-surface-2 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <Flag className="size-3.5 shrink-0 text-accent" aria-hidden="true" />
      <span className="truncate">
        <span className="font-medium text-ink">{parts[0]}</span>
        {parts.slice(1).map((p) => (
          <span key={String(p)}>, {p}</span>
        ))}
      </span>
    </Link>
  )
}
